// Omnix/client/ui/UnifiedInbox.js
import { ComponentBase } from "../core/ComponentBase.js";

export class UnifiedInbox extends ComponentBase {
    static styles = `
        .unified-inbox-container {
            display: flex;
            height: 100%;
            overflow: hidden;
            background-color: var(--bs-body-bg);
            position: relative;
        }
        .conversation-list-pane {
            width: 380px;
            min-width: 320px;
            border-right: 1px solid var(--bs-border-color);
            flex-shrink: 0;
        }
        .conversation-timeline-pane {
            flex-grow: 1;
        }
        .contextual-sidebar-pane {
            flex-shrink: 0;
        }

                .unified-inbox-scroll-to-bottom-btn {
            position: absolute;
            bottom: 100px; /* Position above the composer */
            right: 22rem;
            z-index: 100;
            background-color: var(--bs-primary);
            color: white;
            border: none;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            font-size: 1.2rem;
            display: none; /* Hidden by default */
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
            transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
        }
        .unified-inbox-scroll-to-bottom-btn.visible {
            display: flex;
            opacity: 1;
            transform: scale(1);
        }

        @media (max-width: 768px) {
            .unified-inbox-container.viewing-timeline .conversation-list-pane {
                display: none;
            }
            .unified-inbox-container:not(.viewing-timeline) .conversation-timeline-pane {
                display: none;
            }
            .conversation-list-pane, .conversation-timeline-pane {
                width: 100%;
                border-right: none;
            }
            .unified-inbox-container .contextual-sidebar-pane {
                display: none;
            }
            .unified-inbox-container.viewing-sidebar .contextual-sidebar-pane {
                display: block;
                width: 100%;
            }
            .unified-inbox-container.viewing-sidebar .conversation-list-pane,
            .unified-inbox-container.viewing-sidebar .conversation-timeline-pane {
                display: none;
            }

            .unified-inbox-scroll-to-bottom-btn {
                right: 1rem; /* A bit closer to the edge on mobile */
                bottom: 85px; /* Adjust if composer height is different on mobile */
            }

        }
    `;



    constructor(config) {
        super(config);
        this.activeConversation = null;
        this.isTimelineVisible = false;
        this.isSidebarVisibleMobile = false
        // Bound handlers
        this._boundHandleConversationSelected = this.handleConversationSelected.bind(this);
        this._boundHandleNewConversation = this.handleNewConversation.bind(this);
        this._boundHandleStartConversation = this.handleStartConversation.bind(this);
        this._boundHandleCloseTimeline = this.handleCloseTimeline.bind(this);
        this._boundHandleMessageSend = this.handleMessageSend.bind(this); // Add this line
        this._boundHandleServerEvents = this.handleServerEvents.bind(this);
        this._boundHandleStartConversation = this.handleStartConversation.bind(this);

        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'unified-inbox-container';

        this.conversationList = await this.createManagedComponent('ConversationList', null, { instanceId: `${this.config.instanceId}-list` });
        this.conversationTimeline = await this.createManagedComponent('ConversationTimeline', null, { instanceId: `${this.config.instanceId}-timeline` });
        this.newConversationModal = await this.createManagedComponent('NewConversationModal', null, { instanceId: `${this.config.instanceId}-new-modal` });
        this.emailComposerModal = await this.createManagedComponent('EmailComposerModal', null, {
            instanceId: `${this.config.instanceId}-email-composer`
        });
        this.emailComposerModal.on('emailSendRequested', (payload) => this.handleMessageSend(payload));

        this.conversationTimeline.composer.on('composeEmail', ({ action }) => {
            if (action === 'compose') {
                // For a new email, there is no 'lastInteraction', so we pass null for it,
                // but we pass the activeConversation so the modal knows who to send the email to.
                this.emailComposerModal.show('compose', null, this.activeConversation);
            } else {
                // For replies/forwards, find the last inbound message to quote.
                const lastInteraction = this.conversationTimeline.interactions.slice().reverse().find(i => i.direction === 'inbound');
                if (lastInteraction) {
                    this.emailComposerModal.show(action, lastInteraction, this.activeConversation);
                } else {
                    this.appClient.notifier.warn("No previous message to reply to.");
                }
            }
        });


        this.contextualSidebar = await this.createManagedComponent('ContextualSidebar', null, {
            instanceId: `${this.config.instanceId}-sidebar`
        });
        this.contextualSidebar.on('close', () => this.closeSidebar());
        this.contextualSidebar.on('statusChanged', (e) => this.handleStatusChange(e.status));
        this.contextualSidebar.on('assigneeChanged', (e) => this.handleAssigneeChange(e.assigneeId));
        this.conversationTimeline.on('humanTakeoverRequested', this.handleTakeoverRequest.bind(this));
        this.conversationTimeline.on('handoffAccepted', this.handleHandoffAccept.bind(this));

        // Attach event listeners
        this.conversationList.on('conversationSelected', this._boundHandleConversationSelected);
        this.conversationList.on('newConversationClicked', this._boundHandleNewConversation);
        this.newConversationModal.on('startConversation', this._boundHandleStartConversation);
        this.conversationTimeline.on('sidebarToggled', () => this.openSidebar());
        this.conversationTimeline.on('closeTimeline', this._boundHandleCloseTimeline);
        this.conversationTimeline.on('messageSendRequested', this._boundHandleMessageSend);
        this.conversationTimeline.composer.on('composeEmail', ({ action }) => {
            const lastInteraction = this.conversationTimeline.interactions.slice().reverse().find(i => i.direction === 'inbound');
            if (lastInteraction) {
                this.emailComposerModal.show(action, lastInteraction, this.activeConversation);
            } else {
                this.appClient.notifier.warn("No previous message to reply to.");
            }
        });


        this.ticketForm = await this.createManagedComponent('TicketForm', null, {
            instanceId: `${this.config.instanceId}-ticket-form`
        });

        this.ticketForm.on('ticketSaved', async () => {
            if (this.activeConversation) {
                try {
                    const updatedConversation = await this.appClient.rpcClient.call('UnifiedInboxRpc.getConversationById', {
                        conversationId: this.activeConversation._id
                    });
                    this.activeConversation = updatedConversation; // Update the main active conversation object

                    if (this.contextualSidebar) {
                        // Pass the fresh conversation data to the sidebar to re-render the TicketPanel
                        this.contextualSidebar.setConversation(updatedConversation);
                    }
                } catch (error) {
                    this.log.error("Failed to refresh conversation after ticket save:", error);
                }
            }
        });


        this.linkTicketModal = await this.createManagedComponent('LinkTicketModal', null, {
            instanceId: `${this.config.instanceId}-link-ticket-modal`
        });

        this.linkTicketModal.on('ticketLinked', async ({ ticketId }) => {
            if (this.activeConversation) {
                try {
                    await this.appClient.rpcClient.call('TicketRpc.linkConversation', {
                        ticketId: ticketId,
                        conversationId: this.activeConversation._id
                    });
                    this.appClient.notifier.success("Conversation linked to ticket.");

                    const updatedConversation = await this.appClient.rpcClient.call('UnifiedInboxRpc.getConversationById', {
                        conversationId: this.activeConversation._id
                    });
                    this.activeConversation = updatedConversation;

                    if (this.contextualSidebar) {
                        this.contextualSidebar.setConversation(updatedConversation);
                    }
                } catch (error) {
                    this.log.error("Error linking ticket:", error);
                    this.appClient.notifier.error("Failed to link ticket.");
                }
            }
        });

        this.appClient.events.server.on('unifiedInbox:newInternalNote', (eventData) => {
            if (this.contextualSidebar && this.contextualSidebar.notesPanel) {
                this.contextualSidebar.notesPanel.handleNewNote(eventData.note);
            }
        });



        this.conversationTimeline.on('scrollButtonStateChange', this.handleScrollButtonStateChange.bind(this));
        this.conversationTimeline.on('typingStatusChanged', this.handleTypingStatusChange.bind(this));

        this.appClient.events.server.on('unifiedInbox:newInteraction', this._boundHandleServerEvents);
        this.appClient.events.server.on('unifiedInbox:conversationUpdated', this._boundHandleServerEvents);
        this.appClient.events.server.on('unifiedInbox:typingStatus', this._boundHandleServerEvents);

        await super.initialize();
    }

    async handleStartConversation({ contact, channel }) {
        this.log.info(`Request to start new conversation with contact: ${contact._id} on channel ${channel.type}`);
        try {
            const response = await this.appClient.rpcClient.call('UnifiedInboxRpc.getOrCreateConversationForContactAndChannel', {
                contactId: contact._id,
                channelType: channel.type,
                channelValue: channel.value
            });

            if (response && response.conversation) {
                const conversationObject = {
                    ...response.conversation,
                    contact: response.contact
                };

                this.conversationList.addNewConversation(conversationObject);
                this.handleConversationSelected(conversationObject);

            } else {
                throw new Error("Server did not return a valid conversation object.");
            }
        } catch (error) {
            this.log.error("Failed to start new conversation:", error);
            this.appClient.notifier.error("Could not start the conversation.");
        }
    }


    async handleConversationSelected(conversation) {
        this.activeConversation = conversation;
        this.isTimelineVisible = true;

        this.conversationList.setActiveConversation(conversation._id);

        await this.conversationTimeline.setConversation(conversation);
        this.conversationTimeline.composer.setConversation(conversation);
        this.contextualSidebar.setConversation(conversation);
        this.updateLayout();

        this.conversationTimeline.scrollToBottom(true);

        if (conversation.unreadCount > 0) {
            this.conversationList.resetUnreadCount(conversation._id);
            try {
                const interactionsToMark = this.conversationTimeline.interactions.filter(
                    i => i.direction === 'inbound' && (!i.readBy || !i.readBy.some(r => r.userId === this.appClient.getSharedData('userId')))
                ).map(i => i._id);

                if (interactionsToMark.length > 0) {
                    await this.appClient.rpcClient.call('UnifiedInboxRpc.markAsRead', {
                        conversationId: conversation._id,
                        interactionIds: interactionsToMark
                    });
                }
            } catch (error) {
                this.log.error(`Failed to mark messages as read for conversation ${conversation._id}:`, error);
            }
        }
    }



    handleNewConversation() {
        this.newConversationModal.show();
    }

    handleCloseTimeline() {
        this.activeConversation = null;
        this.isTimelineVisible = false;
        this.isSidebarVisibleMobile = false;

        this.conversationTimeline.setConversation(null);
        this.conversationTimeline.composer.setConversation(null);
        this.contextualSidebar.setConversation(null);

        this.updateLayout();
    }


    async handleMessageSend(payload) {
        if (!this.activeConversation) {
            this.log.error("handleMessageSend called without an active conversation.");
            return;
        }

        try {
            const tempId = `optimistic_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

            const rpcPayload = {
                conversationId: this.activeConversation._id,
                ...payload,
                clientTempId: tempId // Send this ID to the server
            };

            // Pass the tempId to the optimistic update
            this.conversationTimeline.addOptimisticInteraction(rpcPayload);

            await this.appClient.rpcClient.call('UnifiedInboxRpc.sendMessage', rpcPayload);
            // The server will now broadcast the confirmed message with the `clientTempId` included.

        } catch (error) {
            this.log.error("Failed to send message:", error);
            this.appClient.notifier.error("Could not send message.");
            // TODO: Update the optimistic message status to "failed"
        }
    }


    async refresh() {
        const queryParams = this.appClient.layoutEngine.getQueryParams();
        const conversationIdFromUrl = queryParams.conversationId;

        await this.conversationList.loadConversations();

        if (conversationIdFromUrl) {
            // --- THIS IS THE FIX ---
            // Compare against the MongoDB '_id' field, which is what the URL contains.
            const conversationToSelect = this.conversationList.conversations.find(c => c._id === conversationIdFromUrl);
            // --- END OF FIX ---

            if (conversationToSelect) {
                this.log.info(`UnifiedInbox refreshed with conversationId in URL: ${conversationIdFromUrl}. Selecting conversation.`);
                this.handleConversationSelected(conversationToSelect);
            } else {
                this.log.warn(`UnifiedInbox refreshed, but could not find conversation with ID: ${conversationIdFromUrl}`);
            }
        }
    }



    handleServerEvents(eventData) {
        const eventType = eventData.type;
        const conversationId = eventData.conversation?._id || eventData.conversationId;

        this.log.debug(`Received server event for conversation ${conversationId}`, eventData);


        if (eventType === 'conversationUpdated') {
            // An update happened. First, notify the ConversationList so the item updates in the list.
            if (this.conversationList) {
                this.conversationList.handleRealtimeEvent(eventData);
            }

            // NEXT, check if this update is for the CURRENTLY ACTIVE conversation.
            if (this.activeConversation && conversationId === this.activeConversation._id) {
                this.log.info(`Active conversation ${conversationId} was updated. Refreshing views.`);
                // Merge the updates into our local state for the active conversation.
                this.activeConversation = { ...this.activeConversation, ...eventData.updates };

                // Pass the complete, updated conversation object to the child components.
                // This will trigger them to re-render with the new state (e.g., show the handoff banner).
                if (this.conversationTimeline) {
                    this.conversationTimeline.setConversation(this.activeConversation);
                }
                if (this.contextualSidebar) {
                    this.contextualSidebar.setConversation(this.activeConversation);
                }
            }
            return; // The event is fully handled.
        }


        // Add a new case to handle the typing status event
        if (eventType === 'typingStatus') {
            // Route to conversation list to show snippet update
            if (this.conversationList) {
                this.conversationList.handleRealtimeEvent(eventData);
            }
            // Route to active chat view to show header update (if we add it later)
            // if (this.activeConversation && conversationId === this.activeConversation._id && this.conversationTimeline) {
            //     this.conversationTimeline.handleTypingStatusUpdate(eventData);
            // }
            return; // Event handled
        }

        if (eventType === 'newInteraction' && eventData.interaction.clientTempId) {
            const tempId = eventData.interaction.clientTempId;
            if (this.activeConversation && conversationId === this.activeConversation._id) {
                this.conversationTimeline.confirmOptimisticInteraction(tempId, eventData.interaction);
            }
            this.conversationList.handleRealtimeEvent(eventData);
            return;
        }

        if (this.conversationList) {
            this.conversationList.handleRealtimeEvent(eventData);
        }

        if (this.activeConversation && conversationId === this.activeConversation._id) {
            if (this.conversationTimeline) {
                this.conversationTimeline.handleRealtimeEvent(eventData);
                this.conversationTimeline.scrollToBottom();
            }

            if (eventType === 'newInteraction') {
                const interaction = eventData.interaction;

                // Corrected logic:
                if (interaction.direction === 'inbound') {
                    this.conversationList.resetUnreadCount(conversationId);
                    this.appClient.rpcClient.call('UnifiedInboxRpc.markAsRead', {
                        conversationId: conversationId,
                        interactionIds: [interaction._id]
                    }).catch(error => {
                        this.log.error(`Failed to auto-mark new message as read for conversation ${conversationId}:`, error);
                    });
                }
            }
        }
    }


    async handleTakeoverRequest({ conversationId }) {
        if (!this.activeConversation || this.activeConversation._id !== conversationId) return;

        const confirmed = await this.appClient.notifier.confirm(
            "Are you sure you want to take over this conversation from the AI assistant?",
            { title: "Confirm Takeover", okButtonText: "Take Over" }
        );

        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('UnifiedInboxRpc.takeOverConversation', { conversationId });
                this.appClient.notifier.success("You have taken control of the conversation.");
                // The server will broadcast a `conversationUpdated` event, which will trigger a re-render.
            } catch (error) {
                this.log.error("Failed to take over conversation:", error);
                this.appClient.notifier.error(`Could not take over: ${error.message}`);
            }
        }
    }

    async handleHandoffAccept({ conversationId }) {
        if (!this.activeConversation || this.activeConversation._id !== conversationId) return;

        try {
            await this.appClient.rpcClient.call('UnifiedInboxRpc.acceptHandoff', { conversationId });
            this.appClient.notifier.success("You have accepted the handoff.");
            // The server will broadcast a `conversationUpdated` event.
        } catch (error) {
            this.log.error("Failed to accept handoff:", error);
            this.appClient.notifier.error(`Could not accept handoff: ${error.message}`);
        }
    }

    async handleTypingStatusChange({ isTyping }) {
        if (this.activeConversation) {
            try {
                await this.appClient.rpcClient.call('UnifiedInboxRpc.sendTypingIndicator', {
                    conversationId: this.activeConversation._id,
                    isTyping: isTyping
                });
            } catch (error) {
                this.log.error("Failed to send typing indicator:", error);
            }
        }
    }


    updateLayout() {
        this.container.classList.toggle('viewing-timeline', this.isTimelineVisible && !this.isSidebarVisibleMobile);
        this.container.classList.toggle('viewing-sidebar', this.isSidebarVisibleMobile);
    }


    async render() {
        this.container.innerHTML = ''; // Clear before rendering

        const listPane = document.createElement('div');
        listPane.className = 'conversation-list-pane';
        listPane.appendChild(await this.conversationList.render());

        const timelinePane = document.createElement('div');
        timelinePane.className = 'conversation-timeline-pane';
        timelinePane.appendChild(await this.conversationTimeline.render());
        const sidebarPane = document.createElement('div');
        sidebarPane.className = 'contextual-sidebar-pane';
        sidebarPane.appendChild(await this.contextualSidebar.render());

        this.container.appendChild(listPane);
        this.container.appendChild(timelinePane);
        this.container.appendChild(sidebarPane);

        this.scrollToBottomButton = document.createElement('button');
        this.scrollToBottomButton.className = 'unified-inbox-scroll-to-bottom-btn';
        this.scrollToBottomButton.innerHTML = '<i class="bi bi-arrow-down"></i>';
        this.scrollToBottomButton.addEventListener('click', () => {
            this.conversationTimeline.scrollToBottom(true); // Force scroll
        });
        this.container.appendChild(this.scrollToBottomButton);
        this.container.appendChild(await this.newConversationModal.render());

        return this.container;
    }

    handleScrollButtonStateChange({ visible }) {
        if (this.scrollToBottomButton) {
            this.scrollToBottomButton.classList.toggle('visible', visible);
        }
    }


    openSidebar() {
        this.isSidebarVisibleMobile = true;
        this.updateLayout();
    }

    closeSidebar() {
        this.isSidebarVisibleMobile = false;
        this.updateLayout();
    }

    async handleStatusChange(newStatus) {
        if (!this.activeConversation) return;
        try {
            await this.appClient.rpcClient.call('UnifiedInboxRpc.updateConversationDetails', {
                conversationId: this.activeConversation._id,
                updates: { status: newStatus }
            });
            this.appClient.notifier.success("Status updated.");
            // The server event will trigger the UI update for all clients
        } catch (error) {
            this.log.error("Failed to update status:", error);
            this.appClient.notifier.error("Could not update status.");
        }
    }

    async handleAssigneeChange(newAssigneeId) {
        if (!this.activeConversation) return;
        try {
            await this.appClient.rpcClient.call('UnifiedInboxRpc.updateConversationDetails', {
                conversationId: this.activeConversation._id,
                updates: { assigneeId: newAssigneeId }
            });
            this.appClient.notifier.success("Assignee updated.");
        } catch (error) {
            this.log.error("Failed to update assignee:", error);
            this.appClient.notifier.error("Could not update assignee.");
        }
    }


    destroy() {
        this.conversationList?.off('conversationSelected', this._boundHandleConversationSelected);
        this.conversationList?.off('newConversationClicked', this._boundHandleNewConversation);
        this.conversationTimeline?.off('closeTimeline', this._boundHandleCloseTimeline);
        this.conversationTimeline?.off('messageSendRequested', this._boundHandleMessageSend);
        this.appClient.events.server.off('unifiedInbox:newInteraction', this._boundHandleServerEvents);
        this.appClient.events.server.off('unifiedInbox:conversationUpdated', this._boundHandleServerEvents);
        this.appClient.events.server.off('unifiedInbox:typingStatus', this._boundHandleServerEvents);
        super.destroy();
    }
}